import type { IncomingChildLinkRequestItem } from "@services/facilityNetworkLink";
import { abbreviateFacilityType } from "./facilityNetworkChildrenUtils";
import type { FacilityHierarchyInput } from "@utils/facilityUtils";
import { resolveFacilityRegistryDisplayIds, bareNpiFromCcn } from "@utils/facilityUtils";

const REQUEST_TTL_DAYS = 30;
export const INVITE_TTL_DAYS = 15;

export type ParentLinkRequestBannerData = {
  requestId: number;
  parentFacilityName: string;
  npi?: string;
  ccn?: string;
  expiresInDays?: number;
  sentToAdmins?: boolean;
  parentFacilityType?: string;
  parentState?: string;
  parentCity?: string;
  parentLocation?: string;
  parentFacilityGroupId?: string;
  /** AB#10946 — conflict-aware accept CTA */
  coordinationMode?: boolean;
  currentParentFacilityName?: string | null;
};

export function expiryDaysFromCreatedAt(
  iso: string | null | undefined,
  ttlDays: number = REQUEST_TTL_DAYS,
): number | null {
  if (!iso) return null;
  const created = new Date(iso);
  if (Number.isNaN(created.getTime())) return null;
  const expiresAt = new Date(created);
  expiresAt.setDate(expiresAt.getDate() + ttlDays);
  const daysLeft = Math.ceil((expiresAt.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
  return daysLeft > 0 ? daysLeft : 0;
}

export function mapIncomingLinkRequestToBanner(
  request: IncomingChildLinkRequestItem,
): ParentLinkRequestBannerData {
  const ids = resolveFacilityRegistryDisplayIds({
    npi_number: request.parent_npi || request.target_npi,
    ccn_number: request.parent_ccn,
  });
  const npi = ids.npi || bareNpiFromCcn(request.parent_npi || request.target_npi);
  return {
    requestId: request.id,
    parentFacilityName: request.parent_facility_name,
    npi: npi || undefined,
    ccn: ids.ccn || undefined,
    expiresInDays: expiryDaysFromCreatedAt(request.created_at) ?? undefined,
    sentToAdmins: true,
    parentFacilityType: request.parent_facility_type ?? undefined,
    parentState: request.parent_state ?? undefined,
    parentCity: request.parent_city ?? undefined,
    parentLocation: request.parent_location ?? undefined,
    parentFacilityGroupId: request.parent_facility_group_id ?? undefined,
    coordinationMode: Boolean(request.coordination_mode),
    currentParentFacilityName: request.current_parent_facility_name ?? null,
  };
}

export function parentFacilityDisplayName(request: ParentLinkRequestBannerData): string {
  const location =
    request.parentLocation ||
    [request.parentCity, request.parentState].filter(Boolean).join(", ");
  if (location) return `${request.parentFacilityName} — ${location}`;
  return request.parentFacilityName;
}

export function parentTypeAbbrev(request: ParentLinkRequestBannerData): string {
  if (!request.parentFacilityType) return "—";
  const fakeFacility = {
    facility_type: request.parentFacilityType,
    facility_type_slug: request.parentFacilityType.toLowerCase().replace(/\s+/g, "_"),
  } as FacilityHierarchyInput;
  return abbreviateFacilityType(fakeFacility);
}
